import { useState } from 'react';
import { LayoutDashboard, Search, Briefcase, FileText, Video, Bookmark, GraduationCap, Settings, Bell, LogOut, Moon, Sun, Award, User, Lock, CreditCard, ShieldOff, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useDirection } from '@/hooks/useDirection';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { useSeekApplications } from '@/hooks/useSeekApplications';
import { useSeekNotifications, type CandidateNotification } from '@/hooks/useSeekNotifications';
import { Progress } from '@/components/ui/progress';
import { useTheme } from 'next-themes';
import { useSeekServiceGovernance } from '@/hooks/useSeekServiceGovernance';
import { cn } from '@/lib/utils';
import { getCompletionColorClasses } from '@/lib/completionColors';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

/** Maps nav items to their governing service key (if any) */
const SERVICE_KEY_MAP: Record<string, string> = {
  '/seek/cv': 'cv_builder',
  '/seek/jobs': 'job_discovery',
  '/seek/interview-practice': 'interview_practice',
};

type SeekSidebarCountKey = 'jobs' | 'apps' | 'interviews';

interface SeekSidebarNavItem {
  titleKey: string;
  url: string;
  icon: typeof LayoutDashboard;
  countKey?: SeekSidebarCountKey;
  comingSoon?: boolean;
}

const mainNavItems: SeekSidebarNavItem[] = [
  { titleKey: 'seek.nav.dashboard', url: '/seek', icon: LayoutDashboard },
  { titleKey: 'seek.nav.profile', url: '/seek/profile', icon: User },
  { titleKey: 'seek.nav.cv', url: '/seek/cv', icon: FileText },
  { titleKey: 'seek.nav.findJobs', url: '/seek/jobs', icon: Search, countKey: 'jobs' as const },
  { titleKey: 'seek.nav.applications', url: '/seek/applications', icon: Briefcase, countKey: 'apps' as const },
  { titleKey: 'seek.nav.interviewPractice', url: '/seek/interview-practice', icon: Video, countKey: 'interviews' as const },
  { titleKey: 'seek.nav.savedJobs', url: '/seek/saved-jobs', icon: Bookmark },
  { titleKey: 'seek.nav.learning', url: '/seek/learning', icon: GraduationCap, comingSoon: true },
  { titleKey: 'seek.nav.subscription', url: '/seek/settings/subscription', icon: CreditCard },
  { titleKey: 'seek.nav.privacy', url: '/seek/settings', icon: Settings },
];

const seekLogoAssets = {
  en: {
    light: '/SVG-New/Seek-Black%20En.svg',
    dark: '/SVG-New/Seek-White%20En.svg',
  },
  ar: {
    light: '/SVG-New/Seek-Black%20Ar.svg',
    dark: '/SVG-New/Seek-White%20Ar.svg',
  },
} as const;

const forkSeekFMarkSrc = '/brand/forkseek/mark.svg';

const seekMobilePrimaryUrls = ['/seek', '/seek/jobs', '/seek/applications', '/seek/cv'];

function getSeekInitials(name?: string | null) {
  return name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() ?? '?';
}

function isSeekPathActive(pathname: string, url: string) {
  if (url === '/seek' || url === '/seek/settings') {
    return pathname === url;
  }

  return pathname.startsWith(url);
}

export function SeekSidebar() {
  const { state, toggleSidebar, isMobile } = useSidebar();
  const collapsed = !isMobile && state === 'collapsed';
  const { signOut, profile: authProfile } = useSeekAuth();
  const { completionScore: completionPct } = useProfileCompletion();
  const { applications } = useSeekApplications();
  const { notifications } = useSeekNotifications();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isRtl } = useDirection();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const isArabic = (i18n.resolvedLanguage ?? i18n.language)?.startsWith('ar');
  const isDark = (resolvedTheme ?? theme) === 'dark';
  const seekLogoSrc = seekLogoAssets[isArabic ? 'ar' : 'en'][isDark ? 'dark' : 'light'];
  const collapseLabel = collapsed
    ? t('common.openSidebar')
    : t('common.collapseSidebar');

  // Governance state for governed nav items
  const cvBuilderGov = useSeekServiceGovernance('cv_builder');
  const jobDiscoveryGov = useSeekServiceGovernance('job_discovery');
  const interviewPracticeGov = useSeekServiceGovernance('interview_practice');

  const govStateMap: Record<string, ReturnType<typeof useSeekServiceGovernance>> = {
    cv_builder: cvBuilderGov,
    job_discovery: jobDiscoveryGov,
    interview_practice: interviewPracticeGov,
  };

  const unreadCount = notifications?.filter((n: CandidateNotification) => !n.read_at).length ?? 0;

  const counts: Record<string, number> = {
    apps: applications?.length ?? 0,
    interviews: 0, // placeholder
    jobs: 0, // placeholder
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const initials = getSeekInitials(authProfile?.full_name);

  return (
    <Sidebar side={isRtl ? 'right' : 'left'} collapsible="icon">
      <SidebarContent className="seek-sidebar-surface flex flex-col h-full bg-card/95">
        {/* Logo */}
        <div className={cn('border-b border-sidebar-border/80 py-4', collapsed ? 'px-2' : 'px-4')}>
          {collapsed ? (
            <div className="flex w-full items-center justify-center">
              <button
                type="button"
                onClick={toggleSidebar}
                className="group relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={collapseLabel}
                title={collapseLabel}
              >
                <img
                  src={forkSeekFMarkSrc}
                  alt=""
                  aria-hidden="true"
                  className="h-8 w-8 object-contain transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0"
                />
                <PanelLeftOpen className="absolute h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
              </button>
            </div>
          ) : (
            <div className="flex w-full items-start justify-between gap-2">
              <div className="flex min-w-0 flex-col items-center justify-center gap-1.5">
                <img src={seekLogoSrc} alt="ForkSeek" className="h-auto w-32 shrink-0" />
                <span className="text-[11px] font-medium text-muted-foreground tracking-wide">
                  {t('seek.sidebar.tagline')}
                </span>
              </div>
              <button
                type="button"
                onClick={toggleSidebar}
                className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex"
                aria-label={collapseLabel}
                title={collapseLabel}
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Profile Strength Card */}
        {!collapsed && (
          <div className="mx-3 my-3 p-3.5 seek-panel-soft">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-seek/[0.12] flex items-center justify-center">
                <Award className="w-4 h-4 text-seek" />
              </div>
              <p className="text-sm font-bold text-foreground">{t('seek.sidebar.profileStrength')}</p>
            </div>
            <Progress value={completionPct} className={cn('h-2 mb-2', getCompletionColorClasses(completionPct).bar)} />
            <p className="text-[11px] text-muted-foreground leading-tight">
              {t('seek.sidebar.completePct', { pct: completionPct })}
            </p>
          </div>
        )}

        {/* Main Navigation */}
        <SidebarGroup className="flex-1">
          <SidebarGroupContent>
            <SidebarMenu className="px-2">
              {mainNavItems.map((item) => {
                const count = item.countKey ? counts[item.countKey] : 0;
                const serviceKey = SERVICE_KEY_MAP[item.url];
                const gov = serviceKey ? govStateMap[serviceKey] : null;
                const isGoverned = gov?.isGlobalOff === true;

                const itemLabel = t(item.titleKey);

                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild tooltip={itemLabel}>
                      {item.comingSoon ? (
                        <div
                          className={cn(
                            'flex w-full items-center rounded-lg py-2 text-sm text-muted-foreground/50 cursor-default hover:bg-seek/5 transition-colors',
                            collapsed ? 'justify-center px-0' : 'gap-2.5 px-3'
                          )}
                          aria-label={itemLabel}
                          title={itemLabel}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="flex-1">{itemLabel}</span>
                              <span className="text-[10px] font-semibold bg-seek/10 text-seek rounded-full px-2 py-0.5">
                                {t('seek.sidebar.comingSoon')}
                              </span>
                            </>
                          )}
                        </div>
                      ) : (
                        <NavLink
                          to={item.url}
                          end={item.url === '/seek' || item.url === '/seek/settings'}
                          className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm transition-colors ${isGoverned ? 'text-muted-foreground/50 hover:bg-muted/30' : 'text-muted-foreground hover:bg-seek/10 hover:text-seek active:bg-seek/20'}`}
                          activeClassName={isGoverned ? 'bg-muted text-muted-foreground font-medium' : 'bg-seek text-white font-semibold shadow-sm shadow-seek/20'}
                          aria-label={itemLabel}
                          title={itemLabel}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="flex-1">{itemLabel}</span>
                              {isGoverned ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <ShieldOff className="w-3.5 h-3.5 text-destructive/60 shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="max-w-[200px] text-xs">
                                    {gov?.message || t('common.serviceUnavailable')}
                                  </TooltipContent>
                                </Tooltip>
                              ) : count > 0 ? (
                                <span className="text-[11px] font-medium bg-muted rounded-md px-1.5 py-0.5 text-muted-foreground">
                                  {count}
                                </span>
                              ) : null}
                            </>
                          )}
                        </NavLink>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Bottom Section */}
        <div className="mt-auto border-t border-border/80 pt-2 pb-3 px-2 space-y-1">
          {/* Notifications */}
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip={t('seek.nav.notifications')}>
                <NavLink
                  to="/seek/notifications"
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                  activeClassName="bg-seek/10 text-seek font-medium"
                  aria-label={t('seek.nav.notifications')}
                  title={t('seek.nav.notifications')}
                >
                  <Bell className="h-4 w-4 shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{t('seek.nav.notifications')}</span>
                      {unreadCount > 0 && (
                        <span className="text-[11px] font-bold bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center">
                          {unreadCount}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>

          {/* User Card */}
          {!collapsed && authProfile && (
            <div
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-seek/10 transition-colors"
              onClick={() => navigate('/seek/account')}
            >
              <div className="w-9 h-9 rounded-full bg-seek/15 text-seek ring-1 ring-seek/20 flex items-center justify-center text-sm font-bold shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{authProfile.full_name}</p>
                <span className="text-[10px] font-bold text-seek bg-seek/10 px-1.5 py-0.5 rounded-sm uppercase tracking-wider">
                  {t('seek.sidebar.member')}
                </span>
              </div>
            </div>
          )}
          {collapsed && authProfile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-seek/15 text-sm font-bold text-seek ring-1 ring-seek/20 transition-colors hover:bg-seek/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => navigate('/seek/account')}
                  aria-label={authProfile.full_name || t('seek.nav.account')}
                  title={authProfile.full_name || t('seek.nav.account')}
                >
                  {initials}
                </button>
              </TooltipTrigger>
              <TooltipContent side={isRtl ? 'left' : 'right'} className="text-xs">
                {authProfile.full_name || t('seek.nav.account')}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Language + Theme Toggle */}
          {!collapsed && (
            <div className="flex items-center gap-1.5 px-3">
              <LanguageSwitcher
                variant="sidebar"
                className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex-1"
              />
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="p-2 rounded-lg text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                aria-label={t('common.toggleTheme')}
                title={t('common.toggleTheme')}
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
          )}

          {/* Sign Out */}
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={handleSignOut}
                className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive px-3"
                aria-label={t('common.signOut')}
                title={t('common.signOut')}
                data-testid="seek-sign-out-button"
                tooltip={t('common.signOut')}
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span>{t('seek.sidebar.signOut')}</span>}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </div>
      </SidebarContent>
    </Sidebar>
  );
}

export function SeekMobileBottomNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const { signOut, profile: authProfile } = useSeekAuth();
  const { applications } = useSeekApplications();
  const { notifications } = useSeekNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  const cvBuilderGov = useSeekServiceGovernance('cv_builder');
  const jobDiscoveryGov = useSeekServiceGovernance('job_discovery');
  const interviewPracticeGov = useSeekServiceGovernance('interview_practice');

  const govStateMap: Record<string, ReturnType<typeof useSeekServiceGovernance>> = {
    cv_builder: cvBuilderGov,
    job_discovery: jobDiscoveryGov,
    interview_practice: interviewPracticeGov,
  };

  const counts: Record<string, number> = {
    apps: applications?.length ?? 0,
    interviews: 0,
    jobs: 0,
  };

  const unreadCount = notifications?.filter((n: CandidateNotification) => !n.read_at).length ?? 0;
  const primaryItems = seekMobilePrimaryUrls
    .map((url) => mainNavItems.find((item) => item.url === url))
    .filter((item): item is SeekSidebarNavItem => Boolean(item));
  const overflowItems = mainNavItems.filter((item) => !seekMobilePrimaryUrls.includes(item.url));
  const initials = getSeekInitials(authProfile?.full_name);
  const moreLabel = t('common.more');
  const accountLabel = t('seek.nav.account');
  const isMoreActive =
    overflowItems.some((item) => isSeekPathActive(location.pathname, item.url)) ||
    ['/seek/account', '/seek/notifications'].some((url) => isSeekPathActive(location.pathname, url));

  const handleSignOut = async () => {
    setMoreOpen(false);
    await signOut();
    navigate('/');
  };

  const renderOverflowItem = (item: SeekSidebarNavItem) => {
    const itemLabel = t(item.titleKey);
    const active = isSeekPathActive(location.pathname, item.url);
    const serviceKey = SERVICE_KEY_MAP[item.url];
    const gov = serviceKey ? govStateMap[serviceKey] : null;
    const isGoverned = gov?.isGlobalOff === true;
    const count = item.countKey ? counts[item.countKey] : 0;

    if (item.comingSoon) {
      return (
        <div
          key={item.url}
          className="flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-foreground/55"
          aria-label={itemLabel}
          title={itemLabel}
        >
          <item.icon className="h-4 w-4 shrink-0" />
          <span className="flex-1">{itemLabel}</span>
          <span className="rounded-full bg-seek/10 px-2 py-0.5 text-[10px] font-semibold text-seek">
            {t('seek.sidebar.comingSoon')}
          </span>
        </div>
      );
    }

    return (
      <Link
        key={item.url}
        to={item.url}
        onClick={() => setMoreOpen(false)}
        className={cn(
          'flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors',
          active ? 'bg-seek/10 font-semibold text-seek' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          isGoverned && 'text-muted-foreground/60'
        )}
        aria-label={itemLabel}
        title={itemLabel}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        <span className="flex-1">{itemLabel}</span>
        {isGoverned ? (
          <ShieldOff className="h-3.5 w-3.5 shrink-0 text-destructive/60" />
        ) : count > 0 ? (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
            {count}
          </span>
        ) : null}
      </Link>
    );
  };

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card md:hidden"
        aria-label={t('seek.nav.mobileNavigation')}
      >
        <div className="grid h-[calc(4.75rem+env(safe-area-inset-bottom))] grid-cols-5 items-start px-2 pb-[env(safe-area-inset-bottom)] pt-2">
          {primaryItems.map((item) => {
            const itemLabel = t(item.titleKey);
            const count = item.countKey ? counts[item.countKey] : 0;

            return (
              <NavLink
                key={item.url}
                to={item.url}
                end={item.url === '/seek'}
                className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                activeClassName="bg-seek/10 text-seek"
                aria-label={itemLabel}
                title={itemLabel}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" />
                  {count > 0 && (
                    <span className="absolute -end-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {count}
                    </span>
                  )}
                </div>
                <span className="max-w-full truncate">{itemLabel}</span>
              </NavLink>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={cn(
              'flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isMoreActive && 'bg-seek/10 text-seek'
            )}
            aria-label={moreLabel}
            title={moreLabel}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-seek/15 text-[10px] font-bold text-seek ring-1 ring-seek/20">
              {initials}
            </span>
            <span>{moreLabel}</span>
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[82dvh] overflow-y-auto rounded-t-2xl border-border bg-card px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 text-card-foreground md:hidden"
        >
          <SheetHeader className="text-start">
            <SheetTitle className="text-base">{accountLabel}</SheetTitle>
          </SheetHeader>
          {authProfile && (
            <Link
              to="/seek/account"
              onClick={() => setMoreOpen(false)}
              className="mt-4 flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:bg-muted/60"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-seek/15 text-sm font-bold text-seek ring-1 ring-seek/20">
                {initials}
              </span>
              <span className="min-w-0 flex-1 text-start">
                <span className="block truncate text-sm font-semibold text-foreground">{authProfile.full_name}</span>
                <span className="text-xs font-medium text-muted-foreground">{t('seek.sidebar.member')}</span>
              </span>
            </Link>
          )}

          <div className="mt-4 grid gap-1">
            {overflowItems.map(renderOverflowItem)}
            <Link
              to="/seek/notifications"
              onClick={() => setMoreOpen(false)}
              className={cn(
                'flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors',
                location.pathname === '/seek/notifications'
                  ? 'bg-seek/10 font-semibold text-seek'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              )}
              aria-label={t('seek.nav.notifications')}
              title={t('seek.nav.notifications')}
            >
              <Bell className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t('seek.nav.notifications')}</span>
              {unreadCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-destructive-foreground">
                  {unreadCount}
                </span>
              )}
            </Link>
          </div>

          <div className="mt-4 border-t border-border pt-3">
            <div className="flex items-center gap-2">
              <LanguageSwitcher
                variant="sidebar"
                className="flex-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              />
              <button
                type="button"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('common.toggleTheme')}
                title={t('common.toggleTheme')}
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-2 flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('common.signOut')}
              title={t('common.signOut')}
              data-testid="seek-mobile-sign-out-button"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span>{t('seek.sidebar.signOut')}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
